import { Suspense, useLayoutEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { fitObjectToSize } from '../locker/kitTextures';
import { TROPHY_GLB_URL } from './characterUrls';

export { TROPHY_GLB_URL };

type Props = {
  position?: [number, number, number];
  active?: boolean;
  scale?: number;
};

function TrophyModel({ height = 0.95 }: { height?: number }) {
  const { scene } = useGLTF(TROPHY_GLB_URL);

  const kit = useMemo(() => {
    const clone = scene.clone(true);
    clone.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = true;
      m.receiveShadow = true;
      m.frustumCulled = false;
      if (Array.isArray(m.material)) {
        m.material = m.material.map((mat) => {
          const c = (mat as THREE.MeshStandardMaterial).clone();
          c.side = THREE.FrontSide;
          if (c.metalness !== undefined) c.metalness = Math.max(c.metalness, 0.55);
          if (c.roughness !== undefined) c.roughness = Math.min(c.roughness, 0.4);
          return c;
        });
      } else if (m.material) {
        const c = (m.material as THREE.MeshStandardMaterial).clone();
        c.side = THREE.FrontSide;
        if (c.metalness !== undefined) c.metalness = Math.max(c.metalness, 0.55);
        if (c.roughness !== undefined) c.roughness = Math.min(c.roughness, 0.4);
        m.material = c;
      }
    });
    return clone;
  }, [scene]);

  useLayoutEffect(() => {
    kit.position.set(0, 0, 0);
    kit.scale.set(1, 1, 1);
    kit.rotation.set(0, 0, 0);
    kit.updateMatrixWorld(true);

    // Sketchfab FBX often Z-up — stand trophy on Y
    const box = new THREE.Box3().setFromObject(kit);
    const size = new THREE.Vector3();
    box.getSize(size);
    if (size.z >= size.y * 0.95) {
      kit.rotation.x = -Math.PI / 2;
      kit.updateMatrixWorld(true);
    }

    fitObjectToSize(kit, height);

    // Base on ground (y=0)
    kit.updateMatrixWorld(true);
    const fitted = new THREE.Box3().setFromObject(kit);
    kit.position.y -= fitted.min.y;
  }, [kit, height]);

  return <primitive object={kit} />;
}

/** Golden cup GLB for post-goal celebration */
export function TrophyMesh({
  position = [0, 1.2, -4] as [number, number, number],
  active = true,
  scale = 1,
}: Props) {
  const root = useRef<THREE.Group>(null);
  const rise = useRef(0);

  useFrame((_, dt) => {
    if (!root.current || !active) return;
    rise.current = Math.min(1, rise.current + dt * 0.85);
    const e = THREE.MathUtils.smoothstep(rise.current, 0, 1);
    root.current.position.y = THREE.MathUtils.lerp(0.15, position[1], e);
    root.current.rotation.y += dt * 0.45;
    root.current.scale.setScalar(scale * (0.35 + e * 0.65));
  });

  if (!active) return null;

  return (
    <group ref={root} position={[position[0], 0.15, position[2]]}>
      <Suspense fallback={null}>
        <TrophyModel height={1.05} />
      </Suspense>
      <pointLight
        position={[0, 0.7, 0.35]}
        intensity={14}
        color="#f0c46a"
        distance={5}
        decay={2}
      />
    </group>
  );
}

useGLTF.preload(TROPHY_GLB_URL);
